/* eslint-disable jsx-a11y/anchor-is-valid */
import { useProjectLimits } from '@api/limits';
import ChatBubbleContext from '@components/DevRev/SupportBubbleContext';
import { ErrorOutlineOutlined } from '@mui/icons-material';
import {
	alpha,
	Box,
	Button,
	Link,
	List,
	ListItem,
	ListItemButton,
	Menu,
	Typography
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { getLimitDetails } from '@utils/limits';
import React, { useCallback, useContext, useMemo } from 'react';
import { ProjectContext } from 'routes/ProjectProvider';
import { IChatBubbleContext } from 'types/devRev';
import { LimitType } from 'types/limits';
import { ThemeMode } from 'types/theme';
import ProjectNotificationListItem from './ProjectNotificationListItem';

const ProjectNotifications = () => {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

	const { currentProject } = useContext(ProjectContext);
	const { data } = useProjectLimits(currentProject);
	const { open } = React.useContext<IChatBubbleContext>(ChatBubbleContext);
	const isOpen = Boolean(anchorEl);

	const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleCloseMenu = useCallback(() => {
		setAnchorEl(null);
	}, [setAnchorEl]);

	const handleSupportClick = useCallback(() => {
		setAnchorEl(null);
		open();
	}, [setAnchorEl, open]);
	const limits = useMemo(() => {
		const limit: JSX.Element[] = [];
		if (data?.mauExceeded) {
			limit.push(
				<ProjectNotificationListItem
					key="users"
					{...getLimitDetails(LimitType.Users)}
				/>
			);
		}
		if (data?.textExceeded) {
			limit.push(
				<ProjectNotificationListItem
					key="sms"
					{...getLimitDetails(LimitType.SMS)}
				/>
			);
		}
		if (data?.tenantExceeded) {
			limit.push(
				<ProjectNotificationListItem
					key="tenants"
					{...getLimitDetails(LimitType.Tenants)}
				/>
			);
		}
		if (data?.samlExceeded) {
			limit.push(
				<ProjectNotificationListItem
					key="saml"
					{...getLimitDetails(LimitType.SAML)}
				/>
			);
		}
		if (data?.accessKeyExceeded) {
			limit.push(
				<ProjectNotificationListItem
					key="accessKeys"
					{...getLimitDetails(LimitType.AccessKeys)}
				/>
			);
		}
		return limit;
	}, [data]);

	if (!data || limits.length === 0) return null;

	return (
		<Box>
			<Button
				onClick={handleOpenMenu}
				data-testid="project-notifications"
				color="inherit"
				variant="contained"
				disableElevation
				size="small"
				sx={{
					backgroundColor: 'transparent',
					minWidth: 20,
					marginRight: '10px',
					'&:hover': {
						backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2)
					},
					'&:active': {
						backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1)
					}
				}}
			>
				<ErrorOutlineOutlined fontSize="small" color="error" />
			</Button>
			<Menu
				id="project-notification-menu"
				data-testid="project-notification-menu"
				anchorEl={anchorEl}
				open={isOpen}
				anchorOrigin={{
					vertical: 'center',
					horizontal: 'right'
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right'
				}}
				onClose={handleCloseMenu}
				PaperProps={{ sx: { borderRadius: 1 } }}
				MenuListProps={{ sx: { p: 0, maxWidth: 350 } }}
			>
				{isOpen ? (
					<Box>
						<List sx={{ p: 0 }}>
							{limits}
							<ListItem sx={{ p: 0 }}>
								<ListItemButton
									onClick={handleSupportClick}
									data-testid="project-notification-support-button"
									sx={{
										p: 2,
										bgcolor: (theme) =>
											theme.palette.mode === ThemeMode.dark
												? grey[800]
												: grey[100]
									}}
								>
									<Typography color="text.secondary">
										<Link component="span">
											{intl.tr('project.notifications.limit.support.click')}
										</Link>
										{intl.tr('project.notifications.limit.support.contact')}
									</Typography>
								</ListItemButton>
							</ListItem>
						</List>
					</Box>
				) : null}
			</Menu>
		</Box>
	);
};

export default ProjectNotifications;
